import { Home, MessageCircle, type LucideIcon } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { NavMain } from '@/components/nav-main';
import { NavUser } from '@/components/nav-user';
import { TeamSwitcher } from '@/components/team-switcher';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { ChatHistorySidebar } from '@/modules/chat/components/ChatHistorySidebar';
import { UserRole } from '@/modules/common/types';

import { NavProjects } from './nav-projects';

export interface Team {
  name: string;
  logo: LucideIcon;
  plan: 'unlimited' | 'materiality';
}

export interface NavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  isActive?: boolean;
  items: {
    title: string;
    url: string;
  }[];
}

interface NavSection {
  label: string;
  items: NavItem[];
}

interface User {
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  avatar: string;
}

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  user: User;
  teams: Team[];
  navSections: NavSection[];
  isProjectEnabled: boolean;
  userRole?: UserRole;
  showChatSwitcher?: boolean;
  sidebarMode?: 'home' | 'chat';
  onSelectHome?: () => void;
  onSelectChat?: () => void;
}

function SidebarModeSwitcher({
  mode,
  onSelectHome,
  onSelectChat,
}: {
  mode: 'home' | 'chat';
  onSelectHome: () => void;
  onSelectChat: () => void;
}) {
  const { t } = useTranslation();

  const items = [
    {
      value: 'home' as const,
      label: t('modules.mavue2.navigation.home'),
      icon: Home,
      onClick: onSelectHome,
    },
    {
      value: 'chat' as const,
      label: t('modules.mavue2.navigation.chat'),
      icon: MessageCircle,
      onClick: onSelectChat,
    },
  ];

  return (
    <div className="px-3 pb-1 pt-2 group-data-[collapsible=icon]:hidden">
      <div className="grid grid-cols-2 gap-1 rounded-full border border-sidebar-border bg-sidebar-accent/50 p-1">
        {items.map((item) => {
          const isActive = mode === item.value;
          const Icon = item.icon;

          return (
            <button
              key={item.value}
              type="button"
              aria-pressed={isActive}
              onClick={item.onClick}
              className={cn(
                'flex h-8 min-w-0 items-center justify-center gap-1.5 rounded-full px-3 text-xs font-medium transition-colors',
                isActive
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-sidebar-foreground/70 hover:bg-background/70 hover:text-sidebar-foreground'
              )}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function AppSidebar({
  user,
  teams,
  navSections,
  isProjectEnabled,
  userRole,
  showChatSwitcher = false,
  sidebarMode = 'home',
  onSelectHome,
  onSelectChat,
  ...props
}: AppSidebarProps) {
  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <TeamSwitcher teams={teams} />
      </SidebarHeader>
      {showChatSwitcher && onSelectHome && onSelectChat && (
        <SidebarModeSwitcher
          mode={sidebarMode}
          onSelectHome={onSelectHome}
          onSelectChat={onSelectChat}
        />
      )}
      <SidebarContent>
        {showChatSwitcher && sidebarMode === 'chat' ? (
          <ChatHistorySidebar />
        ) : (
          <>
            {navSections.map((section) => (
              <NavMain key={section.label} label={section.label} items={section.items} />
            ))}
            {/* Hide projects for external users or when projects feature is disabled */}
            {isProjectEnabled && <NavProjects />}
          </>
        )}
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={user} userRole={userRole} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
